import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import MatchCard from "@/components/MatchCard";
import api from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { FileText, Sparkles, Lock, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Matches() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  useNavigate(); // ensure react-router context is initialised for MatchCard's internal nav

  const isFree = ["free", "basic"].includes(user?.subscription_tier || "basic");

  // Mount-only fetch. `api` is a stable module import; setters are stable React refs.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/matches");
        setMatches(data.matches || []);
        if (data.message) setMsg(data.message);
      } finally { setLoading(false); }
    })();
  }, []);

  const handleShortlistChange = (candidateId, shortlisted) => {
    // Update the local card state so the icon stays in sync if the page persists.
    setMatches((prev) => prev.map((m) => (m.candidate_id === candidateId ? { ...m, shortlisted } : m)));
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Your Curated Set</div>
            <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1E1B4B] mt-2">This week&apos;s intelligent matches.</h1>
            <p className="mt-3 text-stone-600 max-w-2xl">A small, thoughtful set — every match with a reason.</p>
          </div>
          <Link
            to="/shortlist"
            data-testid="matches-view-shortlist"
            className="btn-pill inline-flex items-center gap-2 border border-stone-300 text-[#1E1B4B] text-sm px-4 py-2 hover:bg-white"
          >
            <FileText size={14} /> View my Shortlist
          </Link>
        </div>

        {!loading && matches.length > 0 && isFree && (
          <div
            data-testid="matches-upgrade-banner"
            className="mt-6 rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-white p-5 flex flex-col md:flex-row items-start md:items-center gap-4"
          >
            <Lock className="text-amber-700 shrink-0" size={22} />
            <div className="flex-1">
              <div className="font-serif-display text-lg text-[#1E1B4B]">Full Marriage Intelligence Reports are locked on Discovery (Free).</div>
              <div className="text-sm text-stone-600 mt-1">
                You can preview each match&apos;s Compatibility Snapshot below. Upgrade to <strong>Clarity (₹1,999/mo)</strong> to unlock the full AI Report + Kundali × AI Fusion + Family-shareable PDF for every match.
              </div>
            </div>
            <Link to="/subscription" data-testid="matches-upgrade-cta" className="btn-pill text-white text-sm px-5 py-2.5 flex items-center gap-2 shrink-0" style={{ background: "#1E1B4B" }}>
              Upgrade to Clarity <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {loading && <div className="mt-16 text-stone-500">Loading…</div>}
        {!loading && !matches.length && (
          <div className="mt-16 border border-stone-200 rounded-2xl p-10 bg-white text-center">
            <Sparkles className="mx-auto text-amber-700" />
            <p className="mt-4 text-stone-600">{msg || "No matches yet."}</p>
            {msg && msg.toLowerCase().includes("gender") ? (
              <Link to="/profile" data-testid="matches-set-gender-cta" className="btn-pill inline-flex mt-6 text-white text-sm" style={{ background: "#1E1B4B" }}>Update Profile</Link>
            ) : (
              <Link to="/assessment" className="btn-pill inline-flex mt-6 text-white text-sm" style={{ background: "#1E1B4B" }}>Complete Assessment</Link>
            )}
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {matches.map((m, idx) => (
            <MatchCard
              key={m.candidate_id}
              match={m}
              index={idx}
              isFree={isFree}
              onShortlistChange={handleShortlistChange}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
